import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, Navigate, Outlet } from "react-router-dom";
import { HotelProvider } from "../context/HotelContext";
import apiCall from "./index";
import useAdditionalUserData from "../hooks/useAdditionalUserData";
import { ensureArray, safeParseJson } from "../utils/inputSanitizers";

export default function RequireAccountUid() {
  const { accountUid } = useParams();
  const location = useLocation();
  const apiUrl = process.env.REACT_APP_API_URL;
  const enviromentUrl = process.env.REACT_APP_ENVIROMENT;

  const headersSetCookie = localStorage.getItem("headersSetCookie");
  const persistedAccountUid = localStorage.getItem("accountUid");

  const additionalUserDataRaw = localStorage.getItem("additionalUserData");
  const additionalUserData = useAdditionalUserData(additionalUserDataRaw);

  const roles = Array.isArray(additionalUserData?.roles) ? additionalUserData.roles : [];
  const isSuperUser = roles.includes("ROLE_SUPERUSER");

  const [allHotelsRaw, setAllHotelsRaw] = useState(() => localStorage.getItem("allHotels"));
  const allHotels = useMemo(() => {
    return ensureArray(safeParseJson(allHotelsRaw, []));
  }, [allHotelsRaw]);
  const isFetchingAllHotelsRef = useRef(false);
  const accountUidNormalized = useMemo(() => (accountUid ? accountUid.toLowerCase() : null), [accountUid]);

  useEffect(() => {
    const handleAllHotelsUpdated = () => {
      setAllHotelsRaw(localStorage.getItem("allHotels"));
    };

    window.addEventListener("allHotelsUpdated", handleAllHotelsUpdated);
    window.addEventListener("storage", handleAllHotelsUpdated);

    return () => {
      window.removeEventListener("allHotelsUpdated", handleAllHotelsUpdated);
      window.removeEventListener("storage", handleAllHotelsUpdated);
    };
  }, []);

  useEffect(() => {
    if (!isSuperUser || !headersSetCookie || allHotels.length) return;
    if (!apiUrl || isFetchingAllHotelsRef.current) return;

    isFetchingAllHotelsRef.current = true;
    const uuid = localStorage.getItem("uuidSetCookie") || additionalUserData?.uuid;
    const queryParams = "?page_size=1000&include=subscription&originSystem=A2";
    let url = `${apiUrl}/opa-accounts${queryParams}`;

    if (enviromentUrl) {
      url += url.includes("?") ? `&env=${enviromentUrl}` : `?env=${enviromentUrl}`;
    }

    apiCall("get", url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${headersSetCookie}`,
        "X-RealmUuid": uuid,
      },
    })
      .then((response) => {
        const records = Array.isArray(response?.records) ? response.records : [];
        const formattedHotels = records
          .filter((hotel) => hotel?.enabled === true)
          .map((hotel) => ({
            accountName: hotel.name,
            accountUuid: hotel.uuid,
            accountUid: hotel.uid,
            accountCurrency: hotel.currency || "",
            accountSubscriptionName: (hotel.subscription && hotel.subscription.name) || "",
            managePropertiesList: hotel.managePropertiesList === true ? true : false,
          }));

        localStorage.setItem("allHotels", JSON.stringify(formattedHotels));
        setAllHotelsRaw(JSON.stringify(formattedHotels));
        window.dispatchEvent(new CustomEvent("allHotelsUpdated"));
      })
      .catch(() => {
        setAllowed(false);
        setReady(true);
      })
      .finally(() => {
        isFetchingAllHotelsRef.current = false;
      });
  }, [isSuperUser, headersSetCookie, allHotels.length, apiUrl, enviromentUrl, additionalUserData?.uuid]);

  const isAdmin = localStorage.getItem("isAdmin") === "true";

  const [ready, setReady] = useState(false);
  const [allowed, setAllowed] = useState(true);
  const [hotelData, setHotelData] = useState(null);
  const lastCheckedAccountUid = useRef(null);

  const rawPath = location.pathname || "";

  const knownCards = useMemo(() => new Set([
    "bookings", "products", "cabanas", "cabanatypes", "users", "account",
    "permissions", "subscriptions", "accounts", "destinations", "marketing",
    "coupon", "coupon-code", "analytics", "website", "guest-directory",
    "knowledge-base", "logs", "mapping", "guide-portal", "login"
  ]), []);

  const isAccountUidActuallyCard = useMemo(() => {
    if (!accountUidNormalized) return false;
    return knownCards.has(accountUidNormalized);
  }, [accountUidNormalized, knownCards]);

  const shouldFixMissingAccountUid = useMemo(() => {
    if (isAccountUidActuallyCard && persistedAccountUid) return true;
    return !accountUid && rawPath.startsWith("/dashboard") && !rawPath.startsWith("/dashboard/admin");
  }, [accountUid, rawPath, isAccountUidActuallyCard, persistedAccountUid]);

  const fixedPath = useMemo(() => {
    if (!shouldFixMissingAccountUid) return null;
    if (!persistedAccountUid) return null;
    
    if (isAccountUidActuallyCard) {
      const pathAfterDashboard = rawPath.replace(/^\/dashboard\//, '');
      return `/dashboard/${persistedAccountUid}/${pathAfterDashboard}`;
    }
    
    const pathAfterDashboard = rawPath.replace(/^\/dashboard/, '');
    return `/dashboard/${persistedAccountUid}${pathAfterDashboard}`;
  }, [shouldFixMissingAccountUid, persistedAccountUid, rawPath, isAccountUidActuallyCard]);

  const redirectTo = useMemo(() => {
    if (isAdmin) return "/dashboard/admin";
    if (!headersSetCookie) return "/login";
    if (shouldFixMissingAccountUid) return fixedPath || "/";
    
    return null;
  }, [
    isAdmin,
    headersSetCookie,
    shouldFixMissingAccountUid,
    fixedPath,
  ]);

  const redirectState = useMemo(() => {
    if (!headersSetCookie) return { from: location };
    return null;
  }, [headersSetCookie, location]);

  const hotelsWithSameUid = useMemo(() => {
    if (!accountUid || !allHotels.length) return [];
    return allHotels.filter((hotel) => hotel?.accountUid === accountUid);
  }, [accountUid, allHotels]);

  const permissionsByAccount = useMemo(() => {
    return Array.isArray(additionalUserData?.permissionsByAccount)
      ? additionalUserData.permissionsByAccount
      : [];
  }, [additionalUserData]);

  const accountsWithSameUid = useMemo(() => {
    if (!accountUid || !permissionsByAccount.length) return [];
    return permissionsByAccount.filter((account) => account?.accountUid === accountUid);
  }, [accountUid, permissionsByAccount]);

  useEffect(() => {
    if (redirectTo) return;

    if (!accountUid) {
      setReady(true);
      setAllowed(true);
      return;
    }

    if (lastCheckedAccountUid.current === accountUid) {
      setReady(true);
      return;
    }

    let match = null;

    if (isSuperUser) {
      if (!allHotels.length) {
        lastCheckedAccountUid.current = null;
        setAllowed(false);
        setReady(false);
        return;
      }

      lastCheckedAccountUid.current = accountUid;
      if (hotelsWithSameUid.length > 1) {
        const duplicateNames = hotelsWithSameUid.map(h => h.accountName).join(", ");
        throw new Error(`Cannot access this account. Multiple accounts share the same UID "${accountUid}": ${duplicateNames}`);
      }
      
      match = hotelsWithSameUid[0] || null;
    } else {
      lastCheckedAccountUid.current = accountUid;
      if (accountsWithSameUid.length > 1) {
        const duplicateNames = accountsWithSameUid.map(acc => acc.accountName).join(", ");
        throw new Error(`Cannot access this account. Multiple accounts share the same UID "${accountUid}": ${duplicateNames}`);
      }
      
      match = accountsWithSameUid[0] || null;
    }

    if (!match) {
      setAllowed(false);
      setReady(true);
      return;
    }

    if (match.accountUid) localStorage.setItem("accountUid", match.accountUid);
    if (match.accountUuid) localStorage.setItem("uuidSetCookie", match.accountUuid);
    if (match.accountName) localStorage.setItem("selectedHotel", match.accountName);
    if (match.accountCurrency) localStorage.setItem("currencyAccount", match.accountCurrency);
    if (match.accountSubscriptionName != null) localStorage.setItem("accountSubscriptionName", match.accountSubscriptionName);
    if (match.managePropertiesList != null) localStorage.setItem("managePropertiesList", match.managePropertiesList);

    const hotelInfo = {
      accountName: match.accountName || accountUid,
      accountUuid: match.accountUuid,
      accountUid: match.accountUid || accountUid,
      accountCurrency: match.accountCurrency,
      accountSubscriptionName: match.accountSubscriptionName,
      managePropertiesList: match.managePropertiesList,
    };
    setHotelData(hotelInfo);

    window.dispatchEvent(
      new CustomEvent("hotelChanged", {
        detail: { title: match.accountName || accountUid },
      })
    );

    setAllowed(true);
    setReady(true);
  }, [accountUid, redirectTo, isSuperUser, additionalUserData, allHotels]);

  if (redirectTo) return <Navigate to={redirectTo} state={redirectState} replace />;

  if (!ready) return null;

  if (accountUid && !allowed) {
    return <Navigate to={persistedAccountUid ? `/dashboard/${persistedAccountUid}` : "/"} replace />;
  }

  return (
    <HotelProvider value={hotelData}>
      <Outlet />
    </HotelProvider>
  );
}
